import { Address } from '../shared/address.ts';
import { ErrorBase } from '../shared/errors.ts';
import { PaymentAVSResult, PaymentCVVResult } from '../shared/payment.ts';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '../supporters/schema.generated.ts';
import { CommitmentUpdateFailure } from './commitment.ts';

export enum AchAccountType {
  NA = 'NA',
  Checking = 'checking',
  Savings = 'savings',
  Unknown = 'Unknown',
}

export interface PaymentAchAccount {
  accountLastFour: string;
  accountName: string;
  accountNumber: string;
  accountNumberMasked: string;
  routingNumber: string;
  routingNumberMasked: string;
}

export interface PaymentAchAccountDTO {
  accountLastFour: string;
  accountName: string;
  accountNumberMasked: string;
  routingNumberMasked: string;
}

// TODO: Match up the address lines and state values with other address object?
export interface PaymentBillingAddress {
  addressType: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  country: string;
  countryDivision: string;
  postalCode: string;
  // NOTE: These are the fields that SF sends, although I think inCareOf is always null and street has a \n-separated value.
  // street: string;
  // inCareOfAddressLine?: string;
}

export enum PaymentMethodStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export interface PaymentApplePay {
  isAuthorized?: boolean;
  message?: string;
  transactionType?: number;
  authorizationDetails?: string;
}

export interface PaymentPayPal {
  acountEmail?: string;
  payerId?: string;
  authorizationId?: string;
  token?: string;
}

export enum PaymentTypeEnum {
  NA = 'NA',
  ACH = 'ACH',
  ApplePay = 'Apple Pay',
  Check = 'Check',
  CreditCard = 'Credit Card',
  ExistingACH = 'Existing ACH',
  ExistingCreditCard = 'Existing Credit Card',
  PayPal = 'PayPal',
}

export interface PaymentCreditCard {
  authorizationCode?: string | null;
  avsResult?: PaymentAVSResult | null;
  billingAddress?: Address; // TODO: type out this address field
  cardType: string;
  cvvResult?: PaymentCVVResult | null;
  description?: string;
  dpmType?: string;
  dpmAuthDetails?: string;
  expirationMonthYear: string;
  merchantOrderNumber?: string;
  nameOnCard: string;
  processorResponseCode?: string | null;
  processorResponseDate?: string | null;
  token: string;
  lastFour: string;
  transactionType?: string | null;
}

export interface PaymentCreditCardDTO {
  // authorizationCode?: string | null;
  // avsResult?: PaymentAVSResult | null;
  billingAddress?: Address; // TODO: type out this address field
  cardType: string;
  dpmType?: string;
  // cvvResult?: PaymentCVVResult | null;
  description?: string;
  expirationMonthYear: string;
  // merchantOrderNumber?: string;
  nameOnCard: string;
  // processorResponseCode?: string | null;
  // processorResponseDate?: string | null;
  // token: string;
  lastFour: string;
  // transactionType?: string | null;
}

/**
 * Holds encrypted credit card information
 */
export interface EncryptedCreditCardTokenizeRequest {
  /**
   * The Cvv, encrypted
   */
  cvv?: string;
  /**
   * The encrypted card number
   */
  pan: string;
}

/**
 * Data for an tokenized credit card
 */
export interface TokenizedCreditCard {
  /**
   * 1 or 2 digit value. Example: 11
   */
  month: string;
  /**
   * 4 digit value. Example: 2017
   */
  year: string;
  /**
   * The full name on the card
   */
  nameOnCard: string;
  /**
   * The tokenized card
   */
  token: string;
  /**
   * Determines if a cvv value can be used for this credit card.
   * TokenEx only provides a CVV for newly tokenized cards - they
   * store the CVV for 1 day.
   */
  useCvv: boolean;
  /**
   * The payment method id, if the card is a saved payment method
   */
  paymentMethodId?: string | null;
  /**
   * The previous authorization details
   */
  previousAuthDetails?: string | null;
}

/**
 * Credit card billing address
 */
export interface CreditCardBillingAddress {
  /**
   * First line of the address
   */
  address1: string;
  /**
   * The billing address city associated with the credit card
   */
  city: string;
  /**
   * The billing address associated with the credit card
   */
  state: string;
  /**
   * Zip/Postal code
   */
  zip: string;
  /**
   * Country
   */
  country: string;
}

export interface CreditCardAuthorizeRequestCartItem {
  /**
   * Item type, e.g. "LongestWaiting"
   */
  type: string;
  /**
   * The item, represented by fund code
   */
  item: string;
  /**
   * Item type description, e.g. "Child Sponsorship"
   */
  description: string;
  /**
   * The quantity
   */
  quantity: number;
  /**
   * The number of pennies donated.
   */
  price: number;
}

/**
 * Holds a tokenized credit card request
 */
export interface CreditCardAuthorizeRequest {
  /**
   * The transaction amount
   */
  amount: number;
  /**
   * The merchant order number
   */
  merchantOrderNumber: string;
  /**
   * Is the transaction initiated by the merchant?
   */
  isMerchantInitiated: boolean;
  /**
   * The supporter id if known
   */
  supporterId: string;
  /**
   * The session id for Kount fraud scoring
   */
  fraudScoreSessionId?: string;
  /**
   * Email
   */
  email: string;
  /**
   * IP address
   */
  ip: string;
  /**
   * An array of the cart items data
   */
  cart: CreditCardAuthorizeRequestCartItem[];
  /**
   * The billing address
   */
  billingAddress: CreditCardBillingAddress;
  /**
   * The tokenized credit card info
   */
  cardInfo: TokenizedCreditCard;
}

/**
 * Holds the authorization response
 */
export interface CreditCardAuthorizeResponse {
  authorization: string;
  avsResult?: {
    code: string;
    message: string;
    postalMatch: string;
    streetMatch: string;
  };
  cvvResult?: {
    code: string;
    message: string;
  };
  error: string;
  message: string;
  referenceNumber: string;
  success: boolean;
  test: boolean;
  transactionResult: boolean;
  authorizationCode: string;
  responseReasonCode: string;
  responseDate: string;
  authDetails: string;
  fraudScoreResult?: {
    decision: string;
    score: number;
    decisionId: string;
  };
}

export interface ApplePayAuthorizeRequest {
  cartId: string;
  merchantOrderNumber: string;
  paymentDetails: ApplePayJS.ApplePayPayment;
}

interface ApplePayPaymentContact {
  phoneNumber: string;
  /**
   *  An email address for the contact.
   */
  emailAddress: string;
  /**
   * The contact’s given name.
   */
  givenName: string;
  /**
   * The contact’s family name.
   */
  familyName: string;
  /**
   * The phonetic spelling of the contact's given name.
   */
  phoneticGivenName: string;
  /**
   * The phonetic spelling of the contact's family name.
   */
  phoneticFamilyName: string;
  /**
   * The street portion of the address for the contact.
   */
  addressLines: string[];
  /**
   * Additional information associated with the location, typically defined at the city or town level (such as district or neighborhood), in a postal address.
   */
  subLocality: string;
  /**
   * The city for the contact.
   */
  locality: string;
  /**
   * The zip code or postal code, where applicable, for the contact.
   */
  postalCode: string;
  /**
   * The subadministrative area (such as a county or other region) in a postal address.
   */
  subAdministrativeArea: string;
  /**
   * The state for the contact.
   */
  administrativeArea: string;
  /**
   * The name of the country or region for the contact.
   */
  country: string;
  /**
   * The contact’s two-letter ISO 3166 country code.
   */
  countryCode: string;
}

// TODO: Where is this interface used?
export interface ApplePayPayment {
  token: string;
  billingContact: ApplePayPaymentContact;
  shippingContact: ApplePayPaymentContact;
}

export interface ApplePayAuthResponse {
  message?: string;
  cardholderName: string;
  applicationExpirationDate: string;
  applicationPrimaryAccountNumber: string;
  avsResponse: {
    code: string;
    message: string;
  };
  transactionType: string;
  authorization: {
    details: string;
    code: string;
    responseDate: string;
    responseReasonCode: string;
    message: string;
  };
  isAuthorized: boolean;
}

export interface AchValidationErrorItemRes {
  message: string;
  field: string;
  code: string;
}

export interface AchValidationRes {
  isValid: boolean;
  validationErrors: AchValidationErrorItemRes[];
  requestId: string;
  accountNumberToken?: string;
  accountNumberLastFour?: string;
}

export interface AchValidationBody {
  accountNumber: string;
  routingNumber: string;
}

export interface PaymentMethodItem {
  achAccount?: PaymentAchAccount;
  billingAddress?: PaymentBillingAddress;
  creditCard: PaymentCreditCard;
  name: string;
  nickName?: string;
  paymentMethodId: string | null;
  paymentMethodStatus: string;
  paymentType: PaymentTypeEnum;
  // saveCardDetails: boolean; // Anything we receive is "saved"
  /** TODO: verify these values belong on a payment method item: */
  sourceCode?: string;
  // supporterGlobalId: string;
  errors?: ErrorBase[];
}

export interface PaymentMethodItemDTO {
  achAccount?: PaymentAchAccountDTO;
  billingAddress?: PaymentBillingAddress;
  creditCard?: PaymentCreditCardDTO;
  name: string;
  nickName?: string;
  paymentMethodId: string | null;
  paymentMethodStatus: string;
  paymentType: PaymentTypeEnum;
  // saveCardDetails: boolean;
  /** TODO: verify these values belong on a payment method item: */
  // sourceCode?: string;
  // supporterGlobalId: string;
  errors?: ErrorBase[];
}

export type PaymentMethodsDTO = PaymentMethodItemDTO[];

export type PaymentMethodMutationResponse = {
  success: boolean;
  paymentMethod: GraphQLSupporterPaymentMethod;
  commitmentUpdates?: {
    updated: GraphQLCommitment[];
    failed: CommitmentUpdateFailure[];
  };
};
